import React, { useRef, useState, useEffect } from 'react';
import { Modal, Button, Space, message } from 'antd';
import { CameraOutlined, CloseOutlined, SyncOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

/**
 * CameraModal - компонент для захвата фото с камеры устройства
 * @param {boolean} visible - видимость модального окна
 * @param {function} onCapture - callback с захваченным файлом
 * @param {function} onCancel - callback при закрытии
 */
const CameraModal = ({ visible, onCapture, onCancel }) => {
	const { t } = useTranslation();
	const videoRef = useRef(null);
	const canvasRef = useRef(null);
	const streamRef = useRef(null);
	const [loading, setLoading] = useState(false);
	const [facingMode, setFacingMode] = useState('environment'); // 'user' или 'environment'
	const [hasMultipleCameras, setHasMultipleCameras] = useState(false);

	// Запуск камеры
	const startCamera = async () => {
		try {
			setLoading(true);

			// Останавливаем предыдущий поток если есть
			if (streamRef.current) {
				streamRef.current.getTracks().forEach((track) => track.stop());
			}

			let stream = null;

			// Пытаемся подключиться с указанным facingMode
			try {
				stream = await navigator.mediaDevices.getUserMedia({
					video: {
						facingMode: facingMode,
						width: { ideal: 1920 },
						height: { ideal: 1080 },
					},
					audio: false,
				});
			} catch (err) {
				// Если не получилось с facingMode, пробуем без него
				console.warn('Failed with facingMode, trying without:', err);
				stream = await navigator.mediaDevices.getUserMedia({
					video: {
						width: { ideal: 1920 },
						height: { ideal: 1080 },
					},
					audio: false,
				});
			}

			streamRef.current = stream;

			if (videoRef.current) {
				videoRef.current.srcObject = stream;
				await videoRef.current.play();
			}

			// Проверяем количество камер
			const devices = await navigator.mediaDevices.enumerateDevices();
			const videoDevices = devices.filter((device) => device.kind === 'videoinput');
			setHasMultipleCameras(videoDevices.length > 1);

			setLoading(false);
		} catch (error) {
			console.error('Error accessing camera:', error);
			setLoading(false);

			let errorMessage = t('camera.error') || 'Ошибка при доступе к камере';

			if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
				errorMessage =
					t('camera.permissionDenied') ||
					'Доступ к камере запрещен. Проверьте разрешения в браузере.';
			} else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
				errorMessage = t('camera.notFound') || 'Камера не найдена на этом устройстве.';
			} else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
				errorMessage = t('camera.inUse') || 'Камера уже используется другим приложением.';
			} else if (
				error.name === 'OverconstrainedError' ||
				error.name === 'ConstraintNotSatisfiedError'
			) {
				errorMessage = t('camera.constraintError') || 'Камера не поддерживает требуемые параметры.';
			} else if (error.name === 'SecurityError') {
				errorMessage =
					t('camera.securityError') ||
					'Доступ к камере заблокирован по соображениям безопасности. Используйте HTTPS.';
			}

			message.error(errorMessage);
			console.error('Camera error details:', {
				name: error.name,
				message: error.message,
				constraint: error.constraint,
			});

			onCancel();
		}
	};

	// Остановка камеры
	const stopCamera = () => {
		if (streamRef.current) {
			streamRef.current.getTracks().forEach((track) => track.stop());
			streamRef.current = null;
		}
		if (videoRef.current) {
			videoRef.current.srcObject = null;
		}
	};

	// Переключение камеры (фронтальная/задняя)
	const switchCamera = () => {
		setFacingMode((prev) => (prev === 'user' ? 'environment' : 'user'));
	};

	// Захват фото
	const capturePhoto = () => {
		if (!videoRef.current || !canvasRef.current) return;

		const video = videoRef.current;
		const canvas = canvasRef.current;

		// Устанавливаем размер canvas равным размеру видео
		canvas.width = video.videoWidth;
		canvas.height = video.videoHeight;

		// Рисуем текущий кадр видео на canvas
		const context = canvas.getContext('2d');
		context.drawImage(video, 0, 0, canvas.width, canvas.height);

		// Конвертируем canvas в blob
		canvas.toBlob(
			(blob) => {
				if (blob) {
					// Создаем файл из blob
					const file = new File([blob], `camera-photo-${Date.now()}.jpg`, {
						type: 'image/jpeg',
						lastModified: Date.now(),
					});

					// Останавливаем камеру
					stopCamera();

					// Вызываем callback с файлом
					onCapture(file);

					message.success(t('camera.photoTaken') || 'Фото сделано');
				} else {
					message.error(t('camera.captureFailed') || 'Не удалось сделать фото');
				}
			},
			'image/jpeg',
			0.95,
		);
	};

	// Обработка закрытия модального окна
	const handleCancel = () => {
		stopCamera();
		onCancel();
	};

	// Эффект для запуска/остановки камеры при открытии/закрытии
	useEffect(() => {
		if (visible) {
			startCamera();
		} else {
			stopCamera();
		}

		return () => {
			stopCamera();
		};
	}, [visible, facingMode]);

	return (
		<Modal
			open={visible}
			title={t('camera.title') || 'Сделать фото'}
			onCancel={handleCancel}
			width={800}
			centered
			footer={
				<Space>
					{hasMultipleCameras && (
						<Button icon={<SyncOutlined />} onClick={switchCamera} disabled={loading}>
							{t('camera.switchCamera') || 'Переключить камеру'}
						</Button>
					)}
					<Button onClick={handleCancel}>{t('common.cancel') || 'Отмена'}</Button>
					<Button
						type="primary"
						icon={<CameraOutlined />}
						onClick={capturePhoto}
						disabled={loading}
					>
						{t('camera.takePhoto') || 'Сделать фото'}
					</Button>
				</Space>
			}
		>
			<div
				style={{
					width: '100%',
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					gap: '16px',
				}}
			>
				<video
					ref={videoRef}
					autoPlay
					playsInline
					muted
					style={{
						width: '100%',
						maxHeight: '60vh',
						backgroundColor: '#000',
						borderRadius: '8px',
					}}
				/>
				<canvas ref={canvasRef} style={{ display: 'none' }} />

				{loading && (
					<div
						style={{
							padding: '20px',
							textAlign: 'center',
							color: '#666',
						}}
					>
						{t('camera.loading') || 'Загрузка камеры...'}
					</div>
				)}
			</div>
		</Modal>
	);
};

export default CameraModal;
